import { useCallback, useState } from "react";
import Cropper from "react-easy-crop";
import { Loader2, Check, X, ZoomIn, ZoomOut, RotateCw } from "lucide-react";

// =====================================================================
// QRCropper — modal that lets the user crop a freshly-picked image
// down to just the QR-code square before upload.
//
// Why: clinics typically upload a screenshot of their GCash / Maya
// payment screen or a photo of a printed QR card. The image as-is is
// too cluttered to render at checkout. Cropping locks it to a clean
// square that drops cleanly into the checkout receipt template.
//
// Usage:
//   <QRCropper
//     file={file}                      // File chosen via <input type="file">
//     onCancel={() => ...}
//     onCropped={(blob) => upload(blob)}  // returns a PNG Blob ready to upload
//   />
//
// Aspect ratio is locked to 1:1 (QR codes are square).
// =====================================================================

export default function QRCropper({
  file,
  onCancel,
  onCropped,
  title = "Crop QR code",
  description = "Drag to position. Scroll or pinch to zoom. Crop tight to the QR square only.",
  testId = "qr-cropper",
}) {
  const [src, setSrc] = useState(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  // Read the file as a data URL once.
  if (file && !src) {
    const reader = new FileReader();
    reader.onload = (e) => setSrc(e.target?.result ?? null);
    reader.readAsDataURL(file);
  }

  const onCropComplete = useCallback((_area, areaPixels) => {
    setCroppedAreaPixels(areaPixels);
  }, []);

  async function handleConfirm() {
    if (!src || !croppedAreaPixels) return;
    setBusy(true); setError(null);
    try {
      const blob = await getCroppedBlob(src, croppedAreaPixels, rotation);
      onCropped?.(blob);
    } catch (e) {
      setError(e.message ?? "Could not crop image.");
      setBusy(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/70 flex items-center justify-center p-4" data-testid={testId}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl flex flex-col max-h-[90vh]">
        <div className="px-5 py-3 border-b border-slate-200 flex items-center gap-3">
          <h2 className="text-base font-semibold text-slate-900">{title}</h2>
          <p className="text-xs text-slate-500 ml-2">{description}</p>
          <button onClick={onCancel} className="ml-auto p-1 rounded hover:bg-slate-100" aria-label="Close">
            <X className="w-4 h-4 text-slate-500" />
          </button>
        </div>

        <div className="relative w-full bg-slate-100" style={{ height: "55vh", minHeight: 320 }}>
          {src ? (
            <Cropper
              image={src}
              crop={crop}
              zoom={zoom}
              rotation={rotation}
              aspect={1}
              cropShape="rect"
              showGrid={true}
              onCropChange={setCrop}
              onZoomChange={setZoom}
              onCropComplete={onCropComplete}
            />
          ) : (
            <div className="absolute inset-0 flex items-center justify-center text-sm text-slate-400">
              <Loader2 className="w-4 h-4 animate-spin mr-2" />Loading image…
            </div>
          )}
        </div>

        <div className="px-5 py-3 border-t border-slate-200 flex items-center gap-3">
          <button onClick={() => setZoom((z) => Math.max(1, z - 0.2))}
            className="p-1.5 rounded hover:bg-slate-100" aria-label="Zoom out" data-testid="qr-zoom-out">
            <ZoomOut className="w-4 h-4 text-slate-600" />
          </button>
          <input type="range" min={1} max={4} step={0.05} value={zoom}
            onChange={(e) => setZoom(Number(e.target.value))}
            data-testid="qr-zoom-slider"
            className="flex-1 max-w-xs" />
          <button onClick={() => setZoom((z) => Math.min(4, z + 0.2))}
            className="p-1.5 rounded hover:bg-slate-100" aria-label="Zoom in" data-testid="qr-zoom-in">
            <ZoomIn className="w-4 h-4 text-slate-600" />
          </button>
          <button onClick={() => setRotation((r) => (r + 90) % 360)}
            className="ml-2 p-1.5 rounded hover:bg-slate-100" aria-label="Rotate 90°" data-testid="qr-rotate"
            title="Rotate 90°">
            <RotateCw className="w-4 h-4 text-slate-600" />
          </button>

          <div className="ml-auto flex items-center gap-2">
            <button onClick={onCancel} disabled={busy}
              className="px-3 py-1.5 text-xs font-semibold text-slate-600 hover:bg-slate-50 rounded">
              Cancel
            </button>
            <button onClick={handleConfirm} disabled={busy || !croppedAreaPixels}
              data-testid="qr-cropper-confirm"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-white bg-teal-600 hover:bg-teal-700 rounded disabled:opacity-50">
              {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
              Use this crop
            </button>
          </div>
        </div>

        {error ? (
          <div role="alert" className="px-5 py-2 text-xs text-rose-700 bg-rose-50 border-t border-rose-100">{error}</div>
        ) : null}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------
// Canvas-side crop. Loads the data URL into an off-screen Image, draws
// only the cropped region into a fresh canvas, exports as PNG Blob.
// Honors rotation in 90° increments.
// ---------------------------------------------------------------------
async function getCroppedBlob(imageSrc, areaPixels, rotation) {
  const image = await loadImage(imageSrc);
  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("Canvas 2D context unavailable.");

  const rad = (rotation * Math.PI) / 180;
  const sin = Math.abs(Math.sin(rad));
  const cos = Math.abs(Math.cos(rad));

  // First, draw the rotated source onto a sized scratch canvas.
  const scratch = document.createElement("canvas");
  const sctx = scratch.getContext("2d");
  if (!sctx) throw new Error("Canvas 2D context unavailable.");

  scratch.width  = image.width  * cos + image.height * sin;
  scratch.height = image.width  * sin + image.height * cos;
  sctx.translate(scratch.width / 2, scratch.height / 2);
  sctx.rotate(rad);
  sctx.drawImage(image, -image.width / 2, -image.height / 2);

  // Then crop from the scratch using areaPixels (which react-easy-crop
  // computes against the rotated image dimensions).
  canvas.width  = areaPixels.width;
  canvas.height = areaPixels.height;
  ctx.drawImage(
    scratch,
    areaPixels.x, areaPixels.y, areaPixels.width, areaPixels.height,
    0, 0, areaPixels.width, areaPixels.height
  );

  return await new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (blob) resolve(blob);
      else reject(new Error("Failed to produce cropped blob."));
    }, "image/png", 0.95);
  });
}

function loadImage(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload  = () => resolve(img);
    img.onerror = (e) => reject(e);
    img.src = src;
  });
}
